/**
 * Activity Logs Documentation Content
 *
 * DOC-04: Activity Logs documentation explaining what gets logged and how to filter
 * Provides guides for viewing, filtering, and using activity logs for auditing.
 */

export const activityLogsMetadata = {
  id: 'activity-logs-overview',
  title: 'Activity Logs Guide',
  description: 'Track and audit all system activity: who did what, when, and the outcome.',
  category: 'activity',
  roles: ['admin'],
  keywords: [
    'activity', 'logs', 'audit', 'tracking', 'history',
    'who did what', 'changes', 'audit trail', 'user activity',
    'system logs', 'actions', 'events'
  ]
};

export const activityLogsContent = {
  sections: [
    // Section 1: What are Activity Logs?
    {
      heading: 'What are Activity Logs?',
      content: `Activity Logs record every action taken in the system. Think of it as a detailed diary of who did what, when they did it, and what changed.

**Why Activity Logs matter:**
- **Accountability:** See exactly who made changes and when
- **Troubleshooting:** Investigate issues by reviewing recent actions
- **Audit Trail:** Maintain records for compliance and review
- **Transparency:** Ensure all team activities are tracked`,
      tips: [
        'Activity logs are read-only - they cannot be edited or deleted',
        'Logs are kept for 90 days for performance reasons',
        'All times are shown in IST (Indian Standard Time)'
      ]
    },

    // Section 2: What Gets Logged?
    {
      heading: 'What Gets Logged?',
      content: 'The system automatically records these types of activities:',
      subsections: [
        {
          title: 'Pulse Entries',
          items: [
            'Create - When a team leader submits a new pulse entry',
            'Update - When anyone modifies an existing pulse entry',
            'Delete - When a pulse entry is removed (rare)'
          ]
        },
        {
          title: 'Verifications',
          items: [
            'TL Verify - When a team leader marks their entry as verified',
            'Manager Verify - When a manager approves a pulse entry'
          ]
        },
        {
          title: 'Commitments',
          items: [
            'Create - When a new commitment (closure) is entered',
            'Fulfill - When a commitment is marked as fulfilled',
            'Partial Fulfill - When a commitment is partially completed',
            'Update - When commitment details are modified'
          ]
        },
        {
          title: 'Rules',
          items: [
            'Create - When a new notification rule is created',
            'Update - When a rule is modified',
            'Delete - When a rule is removed',
            'Enable/Disable - When a rule is turned on or off'
          ]
        },
        {
          title: 'Notification Jobs',
          items: [
            'Enable/Disable - When a notification job is turned on or off',
            'Timing Change - When a job schedule is modified',
            'Manual Trigger - When a job is manually run'
          ]
        },
        {
          title: 'User Activity',
          items: [
            'Login - When a user signs into the system',
            'Logout - When a user signs out',
            'View - When a user accesses certain reports or dashboards'
          ]
        }
      ]
    },

    // Section 3: How to View Activity Logs
    {
      heading: 'How to View Activity Logs',
      content: 'The Activity Logs page provides a chronological view of all system actions.',
      steps: [
        'Click "Activity Logs" in the sidebar navigation',
        'The page shows the most recent activities at the top',
        'Each row shows: time, user, role, action, entity, path, status, and duration',
        'Use the stat cards at the top to see activity summaries',
        'Click "Refresh" to load the latest activities'
      ],
      subsections: [
        {
          title: 'Understanding the Columns',
          items: [
            'Time - When the action occurred (shown in IST)',
            'User - Who performed the action (name or email)',
            'Role - The user\'s role (Admin, Manager, Team Leader)',
            'Action - What was done (Create, Update, Delete, View, etc.)',
            'Entity - What was acted on (Pulse Entry, Commitment, Rule, etc.)',
            'Path - The API endpoint that was called',
            'Status - HTTP status code (200 = success, 400+ = error)',
            'Duration - How long the action took in milliseconds'
          ]
        }
      ]
    },

    // Section 4: Filtering Activity Logs
    {
      heading: 'Filtering Activity Logs',
      content: 'Use filters to find specific activities quickly.',
      steps: [
        'Find the Filters section at the top of the Activity Logs page',
        'Set your filter criteria (date range, user, action, entity type)',
        'Click "Search" to apply the filters',
        'Results will update to show only matching activities',
        'Clear filters to return to the full list'
      ],
      subsections: [
        {
          title: 'Available Filters',
          items: [
            'Start Date - Show activities from this date onwards',
            'End Date - Show activities up to this date',
            'User Email - Filter to a specific user\'s actions',
            'Action - Filter by action type (Create, Update, Delete, View)',
            'Entity Type - Filter by what was acted on (Pulse Entries, Commitments, etc.)'
          ]
        },
        {
          title: 'Combining Filters',
          description: 'You can use multiple filters at once. For example, filter by "Start Date: January 15" AND "User Email: john@wtf.com" AND "Action: Create" to see all items John created since January 15.'
        }
      ],
      tips: [
        'Date filters default to today - change them to see older activities',
        'User email filter does partial matching (searching "john" finds "john@wtf.com")',
        'Clear one filter by selecting "All" in the dropdown'
      ]
    },

    // Section 5: Understanding Log Entries
    {
      heading: 'Understanding Log Entries',
      content: 'Each log entry contains detailed information about what happened.',
      subsections: [
        {
          title: 'Actor (Who)',
          description: 'The person who performed the action. Shown as their name (if available) or email address. The role badge indicates their permission level (Admin, Manager, Team Leader).'
        },
        {
          title: 'Action (What)',
          description: 'What type of action was performed. Color-coded for quick scanning: Green = Create, Blue = Update, Red = Delete, Gray = View, Purple = Verify.'
        },
        {
          title: 'Entity (On What)',
          description: 'What object was acted on. Shows the entity type (like "pulse-entries") and a shortened ID. You can use this ID to look up the specific record.'
        },
        {
          title: 'Timestamp (When)',
          description: 'The exact date and time the action occurred, shown in IST format. Includes day, month, hours, minutes, and seconds for precise tracking.'
        },
        {
          title: 'Status (Result)',
          description: 'Whether the action succeeded or failed. Green badges (200, 201) mean success. Red badges (400, 401, 500) indicate errors. The number is the HTTP status code.'
        },
        {
          title: 'Duration (How Long)',
          description: 'How many milliseconds the action took. Useful for identifying slow operations. Normal actions take under 500ms.'
        }
      ]
    },

    // Section 6: Using Activity Logs for Auditing
    {
      heading: 'Using Activity Logs for Auditing',
      content: 'Activity Logs are powerful for investigating issues and answering questions about what happened.',
      subsections: [
        {
          title: 'Scenario 1: Who changed this pulse entry?',
          description: `You notice a pulse entry has unexpected values and need to find out who modified it.

**Steps:**
1. Go to Activity Logs
2. Set Entity Type filter to "Pulse Entries"
3. Set Action filter to "Update"
4. Set date range to when the change might have occurred
5. Look for entries matching the pulse entry ID
6. Check the User column to see who made the change`
        },
        {
          title: 'Scenario 2: What did this user do today?',
          description: `A manager wants to see all actions taken by a specific team leader.

**Steps:**
1. Go to Activity Logs
2. Enter the user's email in the User Email filter
3. Set Start Date and End Date to today
4. Click Search
5. Review all actions taken by that user`
        },
        {
          title: 'Scenario 3: When was this rule disabled?',
          description: `A notification rule stopped working and you need to find out when it was disabled.

**Steps:**
1. Go to Activity Logs
2. Set Entity Type filter to "Rules" (or leave as All)
3. Search for "disable" or look for rule-related actions
4. Find the entry where the rule was disabled
5. Check the timestamp and user who made the change`
        }
      ]
    },

    // Section 7: Real-Time Updates
    {
      heading: 'Real-Time Updates',
      content: 'Activity Logs update automatically so you can monitor activity in real-time.',
      subsections: [
        {
          title: 'Auto-Refresh',
          description: 'By default, the Activity Logs page refreshes every 30 seconds. New actions appear at the top of the list automatically. You can toggle auto-refresh on/off using the checkbox in the top-right corner.'
        },
        {
          title: 'Manual Refresh',
          description: 'Click the "Refresh" button at any time to immediately load the latest activities. Use this if you are waiting for a specific action to appear.'
        }
      ],
      tips: [
        'Auto-refresh keeps you updated without manual intervention',
        'Disable auto-refresh if you are analyzing a specific time period',
        'New entries appear at the top - scroll up to see the latest',
        'The page shows 50 entries at a time - use pagination to see more'
      ]
    }
  ]
};

export default activityLogsContent;
